import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Invoice } from 'src/redux/billing/billing.types';
import { ReduxState } from 'src/redux/redux-state';
import { buildBillingInvoiceWebSocketUrl } from '../utils/billing-websocket-url';

export type BillingInvoiceWsEvent = 'created' | 'updated' | 'deleted';

export interface BillingInvoiceWsMessage {
  type: 'invoice_update' | 'connected';
  event?: BillingInvoiceWsEvent;
  invoice_group_id?: string;
  invoice_id?: string | null;
  invoice?: Invoice | null;
}

export interface UseBillingInvoiceWebSocketOptions {
  invoiceGroupId: string;
  enabled?: boolean;
  onInvoiceEvent: (message: BillingInvoiceWsMessage) => void;
  onReconnect?: () => void;
}

const RECONNECT_DELAY_MS = 3000;

export function useBillingInvoiceWebSocket({
  invoiceGroupId,
  enabled = true,
  onInvoiceEvent,
  onReconnect,
}: UseBillingInvoiceWebSocketOptions) {
  const accessToken = useSelector((state: ReduxState) => state.userState.access);
  const [liveUpdatesAvailable, setLiveUpdatesAvailable] = useState(true);
  const hasConnectedOnceRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onInvoiceEventRef = useRef(onInvoiceEvent);
  const onReconnectRef = useRef(onReconnect);

  useEffect(() => {
    onInvoiceEventRef.current = onInvoiceEvent;
  }, [onInvoiceEvent]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!enabled || !invoiceGroupId || !accessToken) {
      return;
    }

    clearReconnectTimer();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const url = buildBillingInvoiceWebSocketUrl(invoiceGroupId, accessToken);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      const wasReconnect = hasConnectedOnceRef.current;
      hasConnectedOnceRef.current = true;
      setLiveUpdatesAvailable(true);
      if (wasReconnect) {
        onReconnectRef.current?.();
      }
    };

    ws.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as BillingInvoiceWsMessage;
        if (payload.type === 'invoice_update') {
          onInvoiceEventRef.current(payload);
        }
      } catch {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      setLiveUpdatesAvailable(false);
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (!enabled) {
        return;
      }
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, RECONNECT_DELAY_MS);
    };
  }, [accessToken, clearReconnectTimer, enabled, invoiceGroupId]);

  useEffect(() => {
    if (!enabled || !invoiceGroupId || !accessToken) {
      setLiveUpdatesAvailable(false);
      return undefined;
    }

    const connectionTimer = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
        setLiveUpdatesAvailable(false);
      }
    }, 8000);

    connect();

    return () => {
      clearTimeout(connectionTimer);
      clearReconnectTimer();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [accessToken, clearReconnectTimer, connect, enabled, invoiceGroupId]);

  return { liveUpdatesAvailable };
}
